import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance, { setAuthToken } from '@/lib/axios';
import { Rocket, LogIn, UserPlus, Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login'); // 'login', 'register'
  
  // Giraud login
  const [giraudEmail, setGiraudEmail] = useState('');
  
  // Standard login
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  
  // Registration
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerFirstName, setRegisterFirstName] = useState('');
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);

  // Giraud passwordless login
  const handleGiraudLogin = async (e) => {
    e.preventDefault();
    
    if (!giraudEmail) {
      toast.error('Entre ton email !');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(`${API}/auth/login`, {
        email: giraudEmail
      }, {
        withCredentials: true
      });

      // Store session token for Safari compatibility
      if (response.data.session_token) {
        setAuthToken(response.data.session_token);
      }

      toast.success(response.data.message || 'Connexion réussie !');
      navigate('/dashboard', { 
        state: { 
          user: response.data.user,
          session_token: response.data.session_token
        }, 
        replace: true 
      });
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Email non reconnu');
    } finally {
      setLoading(false);
    }
  };

  // Standard login with password
  const handleStandardLogin = async (e) => {
    e.preventDefault();
    
    if (!loginEmail || !loginPassword) {
      toast.error('Remplis tous les champs !');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(`${API}/auth/login-password`, {
        email: loginEmail,
        password: loginPassword,
        name: '',
        first_name: ''
      }, {
        withCredentials: true
      });

      const { session_token, user } = response.data;
      
      // Store token
      if (session_token) {
        setAuthToken(session_token);
        console.log('[Login] Token stored');
      }

      toast.success(response.data.message || 'Connexion réussie !');
      
      // Simple redirect - cookie already set
      window.location.href = '/dashboard';
      
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Email ou mot de passe incorrect');
      setLoading(false);
    }
  };

  // Registration
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerEmail || !registerPassword || !registerName || !registerFirstName) {
      toast.error('Remplis tous les champs !');
      return;
    }

    if (registerPassword.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(`${API}/auth/register`, {
        email: registerEmail,
        password: registerPassword,
        name: registerName,
        first_name: registerFirstName
      }, {
        withCredentials: true
      });

      // Store session token for Safari compatibility
      if (response.data.session_token) {
        setAuthToken(response.data.session_token);
      }

      toast.success(response.data.message || 'Compte créé !');
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth login
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#030305] relative overflow-hidden flex items-center justify-center">
      {/* Cyber grid background */}
      <div className="cyber-grid-bg"></div>

      {/* Radial gradient overlay */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/20 via-[#030305] to-black pointer-events-none z-0"></div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-md px-6">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Rocket className="h-12 w-12 text-cyan-400" />
          </div>
          <h1 className="text-4xl font-bold font-['Orbitron'] tracking-wider text-white mb-2">
            ACHAT IMMO <span className="text-cyan-400">GAGNANT</span>
          </h1>
          <p className="text-slate-400">Votre dossier bancaire professionnel</p>
        </div>

        {/* Auth Card */}
        <div className="glass-panel p-6 rounded-lg">
          {/* Tabs */}
          <div className="flex mb-6 bg-black/30 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('login')}
              data-testid="tab-login"
              className={`flex-1 py-2 px-3 text-sm font-semibold rounded-md transition-all ${
                activeTab === 'login'
                  ? 'bg-cyan-500 text-black'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Connexion
            </button>
            <button
              onClick={() => setActiveTab('register')}
              data-testid="tab-register"
              className={`flex-1 py-2 px-3 text-sm font-semibold rounded-md transition-all ${
                activeTab === 'register'
                  ? 'bg-cyan-500 text-black'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Inscription
            </button>
          </div>

          {/* VIP Info Banner */}
          {activeTab === 'login' && (
            <div className="mb-4 p-3 bg-cyan-500/20 border border-cyan-500/30 rounded-lg text-sm text-cyan-300">
              <strong>Membres VIP :</strong> Connectez-vous avec votre email et mot de passe pour un accès illimité.
            </div>
          )}

          {/* Standard Login Tab */}
          {activeTab === 'login' && (
            <div>
              <div className="text-center mb-6">
                <p className="text-slate-400 text-sm">
                  Connectez-vous avec votre compte
                </p>
              </div>

              <form onSubmit={handleStandardLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-slate-300 mb-2 block">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="login-email"
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="email@example.com"
                      className="bg-black/50 border-slate-700 focus:border-cyan-500 text-white pl-10"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="login-password" className="text-slate-300 mb-2 block">
                    Mot de passe
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="login-password"
                      type={showLoginPassword ? 'text' : 'password'}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="••••••••"
                      className="bg-black/50 border-slate-700 focus:border-cyan-500 text-white pl-10 pr-10"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-button"
                  className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase tracking-wider py-5"
                >
                  {loading ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent mr-2"></div>
                      Connexion...
                    </>
                  ) : (
                    <>
                      <LogIn className="mr-2 h-4 w-4" />
                      Se connecter
                    </>
                  )}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-[#0a0a0f] text-slate-500">ou</span>
                </div>
              </div>

              {/* Google Login */}
              <Button
                type="button"
                onClick={handleGoogleLogin}
                data-testid="google-login-button"
                className="w-full bg-white hover:bg-gray-100 text-gray-800 font-semibold py-5"
              >
                <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuer avec Google
              </Button>
            </div>
          )}

          {/* Register Tab */}
          {activeTab === 'register' && (
            <div>
              <div className="text-center mb-6">
                <p className="text-slate-400 text-sm">
                  Créez votre compte pour commencer
                </p>
              </div>

              <form onSubmit={handleRegister} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="register-firstname" className="text-slate-300 mb-2 block text-sm">
                      Prénom
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <Input
                        id="register-firstname"
                        type="text"
                        value={registerFirstName}
                        onChange={(e) => setRegisterFirstName(e.target.value)}
                        placeholder="Jean"
                        className="bg-black/50 border-slate-700 focus:border-cyan-500 text-white pl-10"
                        required
                        data-testid="register-firstname-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="register-name" className="text-slate-300 mb-2 block text-sm">
                      Nom
                    </Label>
                    <Input
                      id="register-name"
                      type="text"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      placeholder="Dupont"
                      className="bg-black/50 border-slate-700 focus:border-cyan-500 text-white"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="register-email" className="text-slate-300 mb-2 block text-sm">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="register-email"
                      type="email"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      placeholder="email@example.com"
                      className="bg-black/50 border-slate-700 focus:border-cyan-500 text-white pl-10"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="register-password" className="text-slate-300 mb-2 block text-sm">
                    Mot de passe
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="register-password"
                      type={showRegisterPassword ? 'text' : 'password'}
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      placeholder="Min. 6 caractères"
                      className="bg-black/50 border-slate-700 focus:border-cyan-500 text-white pl-10 pr-10"
                      required
                      minLength={6}
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showRegisterPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="register-button"
                  className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase tracking-wider py-5"
                >
                  {loading ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent mr-2"></div>
                      Création...
                    </>
                  ) : (
                    <>
                      <UserPlus className="mr-2 h-4 w-4" />
                      Créer mon compte
                    </>
                  )}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-[#0a0a0f] text-slate-500">ou</span>
                </div>
              </div>

              {/* Google Register */}
              <Button
                type="button"
                onClick={handleGoogleLogin}
                data-testid="google-register-button"
                className="w-full bg-white hover:bg-gray-100 text-gray-800 font-semibold py-5"
              >
                <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                S'inscrire avec Google
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          Votre dossier bancaire professionnel en quelques clics ! 🏠
        </p>
      </div>
    </div>
  );
}
